import React, { useMemo, useState } from "react";

import {
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import { AdminDealerSearch } from "./AdminDealerSearch";
import IconTextField from "./IconTextField";
import { TbReceipt } from "react-icons/tb";
import { TbListDetails } from "react-icons/tb";
import { getInvoices } from "../services/InvoiceService";
import JBWDateLabel from "./JBWDateLabel";
import { InvoiceDetailsDialog } from "./InvoiceList";

const InvoiceAdminTools = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dealerId, setDealerId] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [jobNumber, setJobNumber] = useState("");
  const [infoMsg, setInfoMsg] = useState({
    msg: "Select a dealer and click search to load invoices",
    colorCode: "#6EC207",
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);

  const handleDealerSelect = (dealer) => {
    setDealerId(dealer.dealerId);
    setTenantId(dealer.tenantId);
    setRows([]);
    setInfoMsg({
      msg: "Dealer selected. Click search to load invoices",
      colorCode: "#6EC207",
    });
  };

  const handleSearch = () => {
    if (!dealerId || !tenantId || !jobNumber.trim()) return;
    setLoading(true);
    setInfoMsg({ msg: "Please wait while loading invoice data", colorCode: "#FCDE70" });
    setRows([]);
    getInvoices(dealerId, tenantId, jobNumber).then((res) => {
      if (res.data && res.data.length > 0) {
        setInfoMsg(null);
        setRows(res.data);
      } else {
        setInfoMsg({ msg: "No Invoices Found", colorCode: "#B8001F" });
      }
      setLoading(false);
    }).catch(() => {
      setInfoMsg({ msg: "Error loading invoices", colorCode: "#B8001F" });
      setLoading(false);
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleDetails = (params) => {
    setSelectedInvoice({ ...params.row });
    setOpenDetailsDialog(true);
  };

  const handleCloseDetailsDialog = () => {
    setSelectedInvoice(null);
    setOpenDetailsDialog(false);
  };

  const columns = useMemo(() => [
    {
      field: "invoiceId",
      headerName: "Invoice #",
      width: 110,
      sortable: false,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "invoiceStatus",
      headerName: "Status",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "invoiceSummary",
      headerName: "Summary",
      width: 250,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "jobNumber",
      headerName: "Job ID",
      width: 100,
      sortable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "firstName",
      headerName: "Customer Name",
      width: 150,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => `${param.row.firstName} ${param.row.lastName}`,
    },
    {
      field: "location",
      headerName: "Location",
      width: 250,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        const location = param.row.locationId;
        if (!location) return "";
        return (
          <Typography variant="subtitl1" component="span" sx={{ wordWrap: "break-word" }} whiteSpace="normal">
            {`${param.row.addressLine1}${param.row.addressLine2 && param.row.addressLine2 !== "null" ? ", " + param.row.addressLine2 : ""}${param.row.city && param.row.city !== "null" ? ", " + param.row.city : ""}${param.row.state && param.row.state !== "null" ? ", " + param.row.state : ""}${param.row.zipcode && param.row.zipcode !== "null" ? ", " + param.row.zipcode : ""}.`}
          </Typography>
        );
      },
    },
    {
      field: "stCreatedOn",
      headerName: "ST Created On",
      sortable: false,
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        if (!param.value) return "";
        return <JBWDateLabel inputDate={param.value} dateFormat="MMM.DD,YYYY" />;
      },
    },
    {
      field: "stModifiedOn",
      headerName: "ST Modified On",
      sortable: false,
      width: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (param) => {
        if (!param.value) return "";
        return <JBWDateLabel inputDate={param.value} dateFormat="MMM.DD,YYYY" />;
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      cellClassName: "actions",
      headerAlign: "center",
      width: 80,
      align: "center",
      getActions: (params) => [
        <GridActionsCellItem
          icon={<TbListDetails size={20} />}
          label="Details Lookup"
          className="textPrimary"
          color="primary"
          title="Details Lookup"
          onClick={() => handleDetails(params)}
        />,
      ],
      sortable: false,
    },
  ], []);

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <AdminDealerSearch onSelect={handleDealerSelect} />
        <IconTextField
          iconStart={<TbReceipt sx={{ color: "#bdbdbd", fontSize: 20 }} />}
          margin="normal"
          fullWidth
          id="jobNumber"
          placeholder="Job Number"
          name="jobNumber"
          autoComplete="off"
          value={jobNumber}
          onChange={(e) => setJobNumber(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
        />
        <IconButton
          color="primary"
          onClick={handleSearch}
          disabled={loading || !dealerId || !jobNumber.trim()}
          sx={{ border: "1px solid #ccc" }}
        >
          {loading ? (
            <CircularProgress size={20} color="warning" />
          ) : (
            <SearchIcon color="secondary" />
          )}
        </IconButton>
      </Box>
      {infoMsg && (
        <Box sx={{ mb: 1, p: 1, borderRadius: 1, backgroundColor: infoMsg.colorCode }}>
          <Typography variant="body2">{infoMsg.msg}</Typography>
        </Box>
      )}
      <Box sx={{ height: 400 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableColumnFilter={true}
          disableColumnMenu={true}
          getRowId={(row) => `${row.invoiceId}-${row.dealerId}`}
          disableRowSelectionOnClick
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
        />
      </Box>
      {openDetailsDialog && (
        <InvoiceDetailsDialog
          open={openDetailsDialog}
          invoice={selectedInvoice}
          onClose={handleCloseDetailsDialog}
        />
      )}
    </>
  );
};

export default InvoiceAdminTools;
